import { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import {
  Box,
  Container,
  Typography,
  TextField,
  Button,
  Paper,
  Alert,
  Divider,
  CircularProgress,
  FormControlLabel,
  Checkbox,
  FormHelperText
} from '@mui/material';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import GoogleSignIn from '../components/auth/GoogleSignIn';
import axios from 'axios';
import Navbar from '../components/layout/Navbar/Navbar';
import { Footer } from '../components/layout/Footer/footer';

const Signup = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { login } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    confirmPassword: ''
  });
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const [passwordErrors, setPasswordErrors] = useState({
    length: false,
    number: false,
    uppercase: false
  });
  const [showPasswordRequirements, setShowPasswordRequirements] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);

  // Add effect to handle Google auth loading state
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const isGoogleAuth = params.get('google');
    
    if (isGoogleAuth) {
      setGoogleLoading(true);
    }
  }, [location]);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const validatePassword = (password) => {
    const errors = {
      length: password.length < 8,
      number: !/\d/.test(password),
      uppercase: !/[A-Z]/.test(password)
    };
    setPasswordErrors(errors);
    return !Object.values(errors).some(error => error);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!agreedToTerms) {
      setError('You must agree to the Terms and Privacy Policy');
      return;
    }

    // Password validation
    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    if (!validatePassword(formData.password)) {
      setError('Password does not meet requirements');
      return;
    }

    setError('');
    setLoading(true);

    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/api/auth/signup`,
        {
          email: formData.email,
          password: formData.password
        }
      );

      await login(response.data);
      navigate('/dashboard');
    } catch (err) {
      setError(
        err.response?.data?.message || 
        'An error occurred during signup'
      );
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleStart = () => {
    setGoogleLoading(true);
  };

  return (
    <>
      <Helmet>
        <title>Create Account | Instation</title>
        <meta name="description" content="Sign up for Instation to create engaging LinkedIn content and grow your professional network." />
      </Helmet>
      
      <Navbar />
      
      <Box sx={{
        display: 'flex',
        flexDirection: 'column',
        minHeight: '100vh',
        pt: { xs: 8, sm: 8 },
        '& .auth-paper': {
          p: { xs: 3, sm: 4 },
          borderRadius: 2,
          transition: 'all 0.3s ease-in-out',
          '&:hover': {
            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)'
          }
        },
        '& .auth-title': {
          fontWeight: 600,
          mb: 3,
          textAlign: 'center',
          fontSize: { xs: '1.75rem', sm: '2rem' },
          background: 'linear-gradient(45deg, #458EFF, #3672FF)',
          WebkitBackgroundClip: 'text',
          WebkitTextFillColor: 'transparent',
          animation: 'fadeIn 0.6s ease-out',
          fontFamily: 'Poppins'
        },
        '& .auth-form-field': {
          mb: 2,
          '& .MuiOutlinedInput-root': {
            transition: 'all 0.3s ease',
            '&:hover': {
              transform: 'translateY(-2px)',
              '& .MuiOutlinedInput-notchedOutline': {
                borderColor: '#458EFF'
              }
            },
            '&.Mui-focused': {
              transform: 'translateY(-2px)'
            }
          }
        },
        '& .auth-submit-button': {
          mt: 3,
          height: 48,
          borderRadius: 2,
          textTransform: 'none',
          fontSize: '16px',
          fontFamily: 'Poppins',
          fontWeight: 600,
          bgcolor: '#458EFF',
          transition: 'all 0.3s ease',
          '&:hover': {
            bgcolor: '#3672FF',
            transform: 'translateY(-2px)',
            boxShadow: '0 4px 12px rgba(69, 142, 255, 0.2)'
          },
          '&:disabled': {
            bgcolor: '#e0e0e0',
            transform: 'none'
          }
        },
        '& .auth-alternate-button': {
          mt: 2,
          color: 'text.secondary',
          transition: 'all 0.3s ease',
          '&:hover': {
            color: '#458EFF',
            bgcolor: 'rgba(69, 142, 255, 0.08)'
          }
        },
        '& .auth-divider-text': {
          px: 2,
          color: 'text.secondary',
          animation: 'fadeIn 0.8s ease-out'
        },
        '& .forgot-password-link': {
          color: '#458EFF',
          textDecoration: 'none',
          transition: 'all 0.3s ease',
          '&:hover': {
            color: '#3672FF',
            textDecoration: 'underline'
          }
        },
        '@keyframes fadeIn': {
          from: {
            opacity: 0,
            transform: 'translateY(10px)'
          },
          to: {
            opacity: 1,
            transform: 'translateY(0)'
          }
        }
      }}>
        <Container maxWidth="sm" sx={{ flex: 1, py: { xs: 4, sm: 8 } }}>
          <Paper className="auth-paper">
            <Typography 
              variant="h4" 
              className="auth-title"
            >
              Create Account
            </Typography>

            {error && (
              <Alert 
                severity="error" 
                sx={{ mb: 3 }} 
                onClose={() => setError('')}
              >
                {error}
              </Alert>
            )}

            {/* Google Sign In */}
            <Box sx={{ mb: 3 }}>
              <GoogleSignIn onStartAuth={handleGoogleStart} />
            </Box>

            {/* Divider */}
            <Box sx={{ mb: 3, display: 'flex', alignItems: 'center' }}>
              <Divider sx={{ flex: 1 }} />
              <Typography 
                variant="body2" 
                color="text.secondary" 
                className="auth-divider-text"
              >
                or sign up with email
              </Typography>
              <Divider sx={{ flex: 1 }} />
            </Box>

            {/* Signup Form */}
            <form onSubmit={handleSubmit}>
              <TextField
                fullWidth
                label="Email"
                name="email"
                type="email"
                value={formData.email}
                onChange={handleChange}
                required
                className="auth-form-field"
                disabled={loading}
              />

              <TextField
                fullWidth
                label="Password"
                name="password"
                type="password"
                value={formData.password}
                onChange={handleChange}
                required
                className="auth-form-field"
                disabled={loading}
                error={Object.values(passwordErrors).some(error => error)}
                onFocus={() => setShowPasswordRequirements(true)}
                onBlur={() => setShowPasswordRequirements(false)}
              />
              
              {showPasswordRequirements && (
                <FormHelperText 
                  sx={{ 
                    mb: 2,
                    color: 'text.secondary',
                    '& .error': {
                      color: 'error.main'
                    },
                    '& .success': {
                      color: 'success.main'
                    }
                  }}
                >
                  Password must contain:
                  <ul style={{ margin: '4px 0', paddingLeft: '20px' }}>
                    <li className={passwordErrors.length ? 'error' : (formData.password.length >= 8 ? 'success' : '')}>
                      At least 8 characters
                    </li>
                    <li className={passwordErrors.number ? 'error' : (/\d/.test(formData.password) ? 'success' : '')}>
                      At least one number
                    </li>
                    <li className={passwordErrors.uppercase ? 'error' : (/[A-Z]/.test(formData.password) ? 'success' : '')}>
                      At least one uppercase letter
                    </li>
                  </ul>
                </FormHelperText>
              )}

              <TextField
                fullWidth
                label="Confirm Password"
                name="confirmPassword"
                type="password"
                value={formData.confirmPassword}
                onChange={handleChange}
                required
                className="auth-form-field"
                disabled={loading}
              />

              <FormControlLabel
                control={
                  <Checkbox 
                    checked={agreedToTerms}
                    onChange={(e) => setAgreedToTerms(e.target.checked)}
                    color="primary"
                  />
                }
                label={
                  <Typography variant="body2">
                    I agree to the{' '}
                    <Link 
                      to="https://instation.app/terms-and-condition" 
                      target="_blank"
                      rel="noopener noreferrer"
                      className="forgot-password-link"
                    >
                      Terms and Conditions
                    </Link>
                    {' '}and{' '}
                    <Link 
                      to="https://instation.app/privacy" 
                      target="_blank"
                      rel="noopener noreferrer"
                      className="forgot-password-link"
                    >
                      Privacy Policy
                    </Link>
                  </Typography>
                }
                sx={{ mt: 2 }}
              />

              <Button
                type="submit"
                variant="contained"
                fullWidth
                size="large"
                disabled={loading || googleLoading}
                className="auth-submit-button"
              >
                {(loading || googleLoading) ? (
                  <>
                    <CircularProgress 
                      size={20} 
                      sx={{ 
                        color: 'white',
                        position: 'absolute',
                        left: '50%',
                        marginLeft: '-10px'
                      }} 
                    />
                    <span style={{ opacity: 0 }}>Sign Up</span>
                  </>
                ) : 'Sign Up'}
              </Button>
            </form>

            {/* Login link */}
            <Button
              variant="text"
              fullWidth
              onClick={() => navigate('/login')}
              disabled={loading}
              className="auth-alternate-button"
            >
              Already have an account? Log in
            </Button>
          </Paper>
        </Container>
        <Footer />
      </Box>
    </>
  );
};

export default Signup;